import { useState, useCallback, useEffect } from 'react';
import { useCanvasStore } from '../stores/canvasStore.js';
import type { DiscoveryState, DiscoveryHint, DiscoveryLevel } from '../types/discovery.js';

interface UseDiscoveryStateOptions {
  onLevelChange?: (newLevel: DiscoveryLevel) => void;
  onHintReceived?: (hints: DiscoveryHint[]) => void;
}

export function useDiscoveryState(options: UseDiscoveryStateOptions = {}) {
  const [discoveryState, setDiscoveryState] = useState<DiscoveryState>({
    sessionId: `session-${Date.now()}`,
    currentLevel: 'foundation',
    exploredConnections: [],
    unlockedCapabilities: [],
    lastInteractionTime: new Date().toISOString(),
    userEngagementScore: 0
  });
  
  const [discoveryHints, setDiscoveryHints] = useState<DiscoveryHint[]>([]);
  const [highlightQueue, setHighlightQueue] = useState<Array<{
    widgetId: string;
    type: 'widget' | 'connection' | 'area';
    color: string;
    duration: number;
  }>>([]);
  
  const activeCanvas = useCanvasStore(state => state.getActiveCanvas());
  
  // Update discovery state from agent response
  const updateFromAgentResponse = useCallback((response: any) => {
    if (response.discoveryState) {
      setDiscoveryState(prev => {
        const prevLevel = prev.currentLevel;
        
        // Check if level changed and call callback
        if (response.discoveryState.currentLevel !== prevLevel) {
          options.onLevelChange?.(response.discoveryState.currentLevel);
        }
        
        return response.discoveryState;
      });
    }
    
    if (response.discoveryHints && response.discoveryHints.length > 0) {
      setDiscoveryHints(prev => [...prev, ...response.discoveryHints]);
      options.onHintReceived?.(response.discoveryHints);
    }
  }, [options.onLevelChange, options.onHintReceived]);
  
  // Handle hint interaction
  const handleHintClick = useCallback((hint: DiscoveryHint) => {
    // If hint relates to specific widgets, highlight them
    if (hint.relatedWidgets && activeCanvas) {
      hint.relatedWidgets.forEach((widgetType) => {
        const widgets = activeCanvas.widgets.filter(w => w.type === widgetType);
        widgets.forEach(widget => {
          setHighlightQueue(prev => [...prev, {
            widgetId: widget.id,
            type: 'widget',
            color: hint.difficulty === 'easy' ? '#4CAF50' : 
                   hint.difficulty === 'medium' ? '#FF9800' : '#F44336',
            duration: 2000
          }]);
        });
      });
    }
    
    // Track engagement
    setDiscoveryState(prev => ({
      ...prev,
      userEngagementScore: Math.min(1, prev.userEngagementScore + 0.05),
      lastInteractionTime: new Date().toISOString()
    }));
  }, [activeCanvas]);
  
  // Dismiss a hint
  const dismissHint = useCallback((hintTitle: string) => {
    setDiscoveryHints(prev => prev.filter(h => h.title !== hintTitle));
  }, []);
  
  // Clear highlights from queue
  const processHighlight = useCallback(() => {
    if (highlightQueue.length > 0) {
      const [current, ...rest] = highlightQueue;
      setHighlightQueue(rest);
      return current;
    }
    return null;
  }, [highlightQueue]);
  
  // Animate discovery level change
  const animateLevelChange = useCallback((newLevel: DiscoveryLevel) => {
    // Create a celebratory highlight effect
    if (activeCanvas && activeCanvas.widgets.length > 0) {
      // Highlight all widgets briefly with level-appropriate color
      const levelColors = {
        foundation: '#4CAF50',
        curiosity: '#FF9800',
        exploration: '#2196F3',
        mastery: '#9C27B0'
      };
      
      activeCanvas.widgets.forEach((widget, index) => {
        setTimeout(() => {
          setHighlightQueue(prev => [...prev, {
            widgetId: widget.id,
            type: 'area',
            color: levelColors[newLevel],
            duration: 1500
          }]);
        }, index * 100);
      });
    }
  }, [activeCanvas]);
  
  // Track widget creation for discovery
  const trackWidgetCreation = useCallback((widgetType: string) => {
    setDiscoveryState(prev => {
      const capabilities = [...prev.unlockedCapabilities];
      
      // Add capabilities based on widget type
      const capabilityMap: Record<string, string[]> = {
        marketData: ['real-time-data', 'symbol-tracking'],
        impactAnalysis: ['ai-insights', 'sentiment-analysis'],
        impactDashboard: ['data-visualization', 'trend-analysis'],
        impactExplorer: ['ai-insights', 'impact-exploration'],
        portfolioComparison: ['multi-asset-analysis', 'correlation-discovery'],
        riskMetrics: ['risk-assessment', 'portfolio-optimization']
      };
      
      const newCapabilities = capabilityMap[widgetType] || [];
      newCapabilities.forEach(cap => {
        if (!capabilities.includes(cap)) {
          capabilities.push(cap);
        }
      });
      
      return {
        ...prev,
        unlockedCapabilities: capabilities,
        userEngagementScore: Math.min(1, prev.userEngagementScore + 0.02)
      };
    });
  }, []);
  
  // Track connection creation for discovery
  const trackConnection = useCallback((sourceType: string, targetType: string, connectionType: string) => {
    setDiscoveryState(prev => {
      const connection = {
        id: `conn-${Date.now()}`,
        sourceType,
        targetType,
        connectionType,
        timestamp: new Date().toISOString(),
        insightLevel: calculateInsightLevel(sourceType, targetType),
        userEngaged: true
      };
      
      return {
        ...prev,
        exploredConnections: [...prev.exploredConnections, connection],
        userEngagementScore: Math.min(1, prev.userEngagementScore + 0.03)
      };
    });
  }, []);
  
  // Calculate insight level based on connection complexity
  const calculateInsightLevel = (sourceType: string, targetType: string): number => {
    const complexityMap: Record<string, number> = {
      'marketData-impactAnalysis': 1,
      'marketData-impactExplorer': 1,
      'impactAnalysis-impactDashboard': 2,
      'impactAnalysis-portfolioComparison': 2,
      'portfolioComparison-riskMetrics': 3,
      'impactAnalysis-riskMetrics': 3,
      'impactExplorer-riskMetrics': 3
    };
    
    const key = `${sourceType}-${targetType}`;
    return complexityMap[key] || 1;
  };
  
  // Effect to animate level changes
  useEffect(() => {
    if (options.onLevelChange) {
      animateLevelChange(discoveryState.currentLevel);
    }
  }, [discoveryState.currentLevel, animateLevelChange, options.onLevelChange]);
  
  return {
    discoveryState,
    discoveryHints,
    highlightQueue,
    updateFromAgentResponse,
    handleHintClick,
    dismissHint,
    processHighlight,
    trackWidgetCreation,
    trackConnection
  };
}